import { setMomentTime } from 'javascripts/util/time';
import { isEmpty, isNil } from 'lodash';
import moment, { type Moment } from 'moment';
import { jsonFetch } from '../../../javascripts/fetch';
import ApplicationModel, { attr } from '../ApplicationModel';
import Fragment from '../Fragment';
import { model, modelAttribute } from '../modelBuilder';
import type MeetingAttendee from './MeetingAttendee';
import type SharedPage from './SharedPage';
import { HasRecordPath } from './concerns/HasRecordPath';
import { type PageAttributes } from './generated/PageAttributes';
import {
  type DocumentTypeEnum_value,
  type EditorWidthEnum_value,
  type RecordId,
  type SortChildPageTypeEnum_value,
} from './generated/types';
import { DocumentTypeEnum } from '../Enums';
import { RECORD_ICONS } from 'javascripts/record_icons';

interface Page extends PageAttributes, HasRecordPath {}

@model
@HasRecordPath
class Page extends ApplicationModel {
  static DOCUMENT_TYPE_NOTE: DocumentTypeEnum_value = 'NOTE';
  static DOCUMENT_TYPE_WHITEBOARD: DocumentTypeEnum_value = 'WHITEBOARD';
  static DOCUMENT_TYPE_FOLDER: DocumentTypeEnum_value = 'FOLDER';
  static DOCUMENT_TYPE_DAILY_NOTES_FOLDER: DocumentTypeEnum_value =
    'DAILY_NOTES_FOLDER';
  static DOCUMENT_TYPE_MEETING_NOTE: DocumentTypeEnum_value = 'MEETING_NOTE';
  static DOCUMENT_TYPE_MEETING_WHITEBOARD: DocumentTypeEnum_value =
    'MEETING_WHITEBOARD';
  static DOCUMENT_TYPE_MEETING = [
    Page.DOCUMENT_TYPE_MEETING_NOTE,
    Page.DOCUMENT_TYPE_MEETING_WHITEBOARD,
  ] as const;
  static DOCUMENT_TYPE_EXTERNAL_LINK: DocumentTypeEnum_value = 'EXTERNAL_LINK';
  static DOCUMENT_TYPE_INTERNAL_LINK: DocumentTypeEnum_value = 'INTERNAL_LINK';
  static DOCUMENT_TYPE_FILE: DocumentTypeEnum_value = 'FILE';
  static DOCUMENT_TYPE_LINK = [
    Page.DOCUMENT_TYPE_INTERNAL_LINK,
    Page.DOCUMENT_TYPE_EXTERNAL_LINK,
  ] as const;

  static DOCUMENT_TYPES = [
    Page.DOCUMENT_TYPE_NOTE,
    Page.DOCUMENT_TYPE_WHITEBOARD,
    Page.DOCUMENT_TYPE_FOLDER,
    Page.DOCUMENT_TYPE_DAILY_NOTES_FOLDER,
    Page.DOCUMENT_TYPE_MEETING_NOTE,
    Page.DOCUMENT_TYPE_MEETING_WHITEBOARD,
    Page.DOCUMENT_TYPE_INTERNAL_LINK,
    Page.DOCUMENT_TYPE_EXTERNAL_LINK,
    Page.DOCUMENT_TYPE_FILE,
  ] as const;

  static DOCUMENT_TYPES_EXCEPT_LINKS = Page.DOCUMENT_TYPES.filter(
    dt => !Page.DOCUMENT_TYPE_LINK.includes(dt)
  );

  static DEFAULT_EMOJI_ICONS = {
    [Page.DOCUMENT_TYPE_WHITEBOARD]: 'fa-shapes',
    [Page.DOCUMENT_TYPE_DAILY_NOTES_FOLDER]: 'fa-calendar-day',
    [Page.DOCUMENT_TYPE_FOLDER]: 'fa-folder',
    [Page.DOCUMENT_TYPE_MEETING_NOTE]: 'fa-calendar',
    [Page.DOCUMENT_TYPE_MEETING_WHITEBOARD]: 'fa-calendar',
    [Page.DOCUMENT_TYPE_INTERNAL_LINK]: 'fa-link',
    [Page.DOCUMENT_TYPE_EXTERNAL_LINK]: 'fa-link',
  };

  static EDITOR_WIDTH_NARROW: EditorWidthEnum_value = 'NARROW';
  static EDITOR_WIDTH_FULL: EditorWidthEnum_value = 'FULL';

  static DEFAULT_NAME = 'Untitled';

  static terminologyName = 'Note';

  static DocumentTypesByNumber = {
    10: Page.DOCUMENT_TYPE_NOTE,
    20: Page.DOCUMENT_TYPE_WHITEBOARD,
    30: Page.DOCUMENT_TYPE_FOLDER,
    40: Page.DOCUMENT_TYPE_DAILY_NOTES_FOLDER,
    50: Page.DOCUMENT_TYPE_MEETING_NOTE,
    60: Page.DOCUMENT_TYPE_MEETING_WHITEBOARD,
    70: Page.DOCUMENT_TYPE_EXTERNAL_LINK,
    80: Page.DOCUMENT_TYPE_INTERNAL_LINK,
    100: Page.DOCUMENT_TYPE_FILE,
  };

  // Start auto-generated
  static typename: 'Page' = 'Page';
  get typename(): 'Page' {
    return 'Page';
  }

  static internalTypename: 'Page' = 'Page';
  get internalTypename(): 'Page' {
    return 'Page';
  }

  static inputType = 'PageAttributes!';

  static filterType = 'PageFilters';
  static filters = {
    id: '[ID!]',
    projectId: 'ID',
    parentId: 'ID',
    documentDate: 'ISO8601Date',
    documentMonth: 'ISO8601Date',
    documentType: '[DocumentTypeEnum!]',
    watched: 'Boolean',
    personal: 'Boolean',
    attendee: 'Boolean',
    accessedAfter: 'ISO8601DateTime',
  };

  static fields = {
    childrenCount: attr({ readable: true }),
    commentsCount: attr({ readable: true }),
    coverImageUrl: attr({ readable: true }),
    coverImageVerticalPosition: attr({ readable: true }),
    createdAt: attr({ readable: true }),
    descriptionMarkdown: attr({ writable: true, readable: false }),
    documentDate: attr({ writable: true, readable: true }),
    documentFileUrl: attr({ readable: true }),
    documentType: attr({ writable: true, readable: true }),
    editLocked: attr({ writable: true, readable: true }),
    editorVersion: attr({ writable: true, readable: false }),
    editorWidth: attr({ writable: true, readable: true }),
    emojiValue: attr({ writable: true, readable: true }),
    externalUrl: attr({ readable: true }),
    hasHiddenDescendants: attr({ readable: true }),
    id: attr({ readable: true }),
    ideaPortalsCount: attr({ readable: true }),
    inDiscoveryParent: attr({ writable: true, readable: false }),
    internalPath: attr({ readable: true }),
    isShared: attr({ readable: true }),
    knowledgeBasesCount: attr({ readable: true }),
    lastAccessedAt: attr({ readable: true }),
    linkedDocumentType: attr({ readable: true }),
    linkedRecordId: attr({ readable: true }),
    linkedRecordType: attr({ readable: true }),
    location: attr({ writable: true, readable: true }),
    meetingAttendeesCount: attr({ readable: true }),
    meetingDuration: attr({ writable: true, readable: true }),
    name: attr({ writable: true, readable: true }),
    parentId: attr({ readable: true }),
    path: attr({ readable: true }),
    position: attr({ writable: true, readable: true }),
    projectId: attr({ readable: true }),
    publishingState: attr({ readable: true }),
    readOnly: attr({ readable: true }),
    referenceNum: attr({ readable: true }),
    reviewerDocumentAccess: attr({ writable: true, readable: true }),
    shareAsWebpagePath: attr({ readable: true }),
    sharedAsWebpage: attr({ readable: true }),
    sharedPagesCount: attr({ readable: true }),
    showContents: attr({ writable: true, readable: true }),
    thumbnailUrl: attr({ readable: true }),
    updatedAt: attr({ readable: true }),
    watchersCount: attr({ readable: true }),
    workflowStatusId: attr({ readable: true }),
    workspaceRestriction: attr({ readable: true }),
    activeShareLink: attr.belongsTo({
      readable: true,
      typename: 'PageShareLink',
    }),
    addTo: attr.belongsTo({ readable: true, typename: 'AddPageTo' }),
    createdByUser: attr.belongsTo({ readable: true, typename: 'User' }),
    description: attr.belongsTo({
      writable: true,
      readable: true,
      typename: 'Note',
    }),
    export: attr.belongsTo({ readable: true, typename: 'ExportPage' }),
    knowledgeBase: attr.belongsTo({
      readable: true,
      typename: 'KnowledgeBase',
    }),
    parent: attr.belongsTo({
      writeShape: ['id'],
      readable: true,
      typename: 'Page',
    }),
    permissions: attr.belongsTo({
      readable: true,
      typename: 'PageResourcePermissions',
    }),
    project: attr.belongsTo({
      writeShape: ['id'],
      readable: true,
      typename: 'Project',
    }),
    user: attr.belongsTo({ readable: true, typename: 'User' }),
    whiteboard: attr.belongsTo({ readable: true, typename: 'Whiteboard' }),
    workflowStatus: attr.belongsTo({
      readable: true,
      typename: 'WorkflowStatus',
    }),
    children: attr.hasMany({ readable: true }),
    comments: attr.hasMany({ readable: true }),
    customFieldValues: attr.hasMany({
      args: { ids: '[ID!]', keys: '[String!]' },
      readable: true,
    }),
    ideaPortals: attr.hasMany({ readable: true }),
    knowledgeBases: attr.hasMany({ readable: true }),
    meetingAttendees: attr.hasMany({ readable: true }),
    sharedPages: attr.hasMany({ readable: true }),
    tasks: attr.hasMany({ readable: true }),
    watchers: attr.hasMany({ readable: true }),
  };
  // End auto-generated

  get parentId() {
    return this.parent ? this.parent.id : null;
  }

  set parentId(id: RecordId | null) {
    this.parent = id ? new Page({ id }) : null;
  }

  get defaultName() {
    if (this.documentType === Page.DOCUMENT_TYPE_FOLDER) {
      return 'New folder';
    } else if (this.documentDate) {
      return this.documentDateAsDate()!.toLocaleString('default', {
        dateStyle: 'long',
      });
    } else {
      return Page.DEFAULT_NAME;
    }
  }

  get terminologyName() {
    switch (this.documentType) {
      case Page.DOCUMENT_TYPE_WHITEBOARD:
        return 'Whiteboard';
      case Page.DOCUMENT_TYPE_FOLDER:
        return 'Folder';
      default:
        return Page.terminologyName;
    }
  }

  get nameOrDefault() {
    if (this.name && this.name.length > 0) {
      return this.name;
    } else {
      return this.defaultName;
    }
  }

  get isPersonal() {
    return !this.project && !!this.user;
  }

  get canFacilitate() {
    return !this.readOnly && (!this.isPersonal || this.isShared);
  }

  get isShared() {
    if (!isNil(this.getAttributeWithoutInstantiating('isShared'))) {
      return this.getAttribute('isShared');
    }

    return this.activeShareLink || this.sharedPagesCount > 0;
  }

  get isSharable() {
    return [
      Page.DOCUMENT_TYPE_NOTE,
      Page.DOCUMENT_TYPE_WHITEBOARD,
      Page.DOCUMENT_TYPE_MEETING_NOTE,
      Page.DOCUMENT_TYPE_MEETING_WHITEBOARD,
    ].includes(this.documentType);
  }

  get isFolder() {
    return (
      this.documentType === Page.DOCUMENT_TYPE_FOLDER ||
      this.documentType === Page.DOCUMENT_TYPE_DAILY_NOTES_FOLDER
    );
  }

  get isMeeting() {
    return Page.DOCUMENT_TYPE_MEETING.includes(this.documentType);
  }

  get isLink() {
    return Page.DOCUMENT_TYPE_LINK.includes(this.documentType);
  }

  get isExternalLink() {
    return this.documentType === Page.DOCUMENT_TYPE_EXTERNAL_LINK;
  }

  get isInternalLink() {
    return this.documentType === Page.DOCUMENT_TYPE_INTERNAL_LINK;
  }

  get isFile() {
    return this.documentType === Page.DOCUMENT_TYPE_FILE;
  }

  get isWhiteboard() {
    return this.documentType === Page.DOCUMENT_TYPE_WHITEBOARD;
  }

  get isInternalPage() {
    return (
      this.documentType === DocumentTypeEnum.INTERNAL_LINK.value &&
      this.linkedRecordType === 'Page'
    );
  }

  get isHiddenRecord() {
    return this.isInternalLink && !this.linkedRecordId;
  }

  get hasTime() {
    return this.isMeeting && this.documentDate;
  }

  /**
   * We need the same date in local time from our UTC string, so we have to use
   * the component values version of the date constructor.
   */
  documentDateAsDate() {
    if (!this.documentDate) return null;

    if (this.isMeeting) {
      return this.documentTime.toDate();
    }

    const [year, month, rest] = this.documentDate.split('-');
    const day = rest.split('T')[0];

    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  /**
   * @returns The document date as a string
   */
  get documentDate() {
    return this.getAttribute('documentDate') as string;
  }

  /**
   * @param date The document date as a string
   */
  set documentDate(date: string) {
    this.setDocumentDate(date);
  }

  /**
   * Set the document date. This is additional to the documentDate setter
   * because we accept a string or a Date object.
   *
   * This is only for setting the date part. If the document already has a time
   * then the time will be merged in.
   *
   * @param date The document date as a string or Date object
   */
  setDocumentDate(date: string | Date | Moment) {
    const dateMoment = moment(date);

    /**
     * When this is a meeting the time we set on the server is the UTC
     * representation of the datetime. When it's not a meeting then the tiem we
     * set on the server is the date in UTC time.
     *
     * The difference is for non-meetings the server time rendered represents
     * the date the user set, and every user would see the same date regardless
     * of their timezone. So if I set the date to the 11th from NZ time then
     * it's the 11th UTC despite that it would get converted to the 10th.
     *
     * For meetings it would become the 10th and it would get converted to the
     * 11th using my timezone.
     *
     * This is because meetings are scheduled but other pages are just about a
     * date.
     */
    if (this.isMeeting) {
      this.documentTime = setMomentTime(
        dateMoment,
        this.documentTime.clone().utcOffset(dateMoment.utcOffset())
      );
    } else {
      this.setAttribute('documentDate', dateMoment.utc(true).toISOString());
    }
  }

  /**
   * @returns The document time as a moment instance
   */
  get documentTime() {
    return moment.utc(this.documentDate);
  }

  /**
   * Set the documentDate using a moment object
   *
   * @param time The document time as a moment instance
   */
  set documentTime(time: Moment) {
    this.setAttribute('documentDate', time.toISOString(true));
  }

  /**
   * Update the recently viewed for this page
   */
  async trackViewed() {
    // Use jsonFetch so we can use window.aha.active in integration tests
    await jsonFetch(`/pages/${this.referenceNum}.recent_view`, {
      method: 'GET',
    });
  }

  set sharedPages(sharedPages: Array<SharedPage>) {
    this.setAttribute('sharedPages', sharedPages);
  }

  setChildrenCount(count: number) {
    this.setAttribute('childrenCount', count, false);
  }

  /**
   * @param options
   * @param options.sort Either 'name' or 'createdAt', the field to use when sorting
   * @returns true if successful
   */
  async sortChildren(options: {
    sort: SortChildPageTypeEnum_value;
  }): Promise<Boolean> {
    const { sort } = options;

    const data = await ApplicationModel.mutate(
      new Fragment('sortChildPages')
        .argument('id', this.id)
        .argument('sort', sort, 'SortChildPageTypeEnum!')
        .argument('__reactivelyUpdate', true)
    );

    return isEmpty(data.sortChildPages.errors);
  }

  /**
   * Helper getter method to get the templates used in this page's description
   * or whiteboard.
   * @returns {RecordId[]} An array of the template IDs used.
   */
  get usedTemplateIds(): RecordId[] | null {
    if (this.documentType === Page.DOCUMENT_TYPE_NOTE) {
      return this.description?.usedTemplateIds;
    } else if (this.documentType === Page.DOCUMENT_TYPE_WHITEBOARD) {
      return this.whiteboard?.usedTemplateIds ?? null;
    }

    return null;
  }

  async loadUsedTemplateIds(): Promise<RecordId[] | null> {
    await this.reload({
      query: this.query.select('documentType').merge({
        description: ['usedTemplateIds'],
        whiteboard: ['usedTemplateIds'],
      }),
    });

    return this.usedTemplateIds;
  }

  get defaultEmojiIcon() {
    if (this.isInternalLink && this.linkedRecordType !== 'Page') {
      return RECORD_ICONS[this.linkedRecordType] || 'fa-link';
    }

    if (this.isFile) {
      // Currently we only support PDF files.
      return 'fa-file-pdf';
    }

    return (
      Page.DEFAULT_EMOJI_ICONS[this.linkedDocumentType ?? this.documentType] ||
      'fa-file-lines'
    );
  }

  async inviteUser(id: RecordId, attendeeScope: MeetingAttendee['query']) {
    if (!this.isMeeting)
      throw new Error('Cannot invite user to non-meeting page');

    const fragment = new Fragment('inviteUserToMeeting', 'mutation')
      .argument('pageId', this.id)
      .argument('userId', id)
      .fragment(attendeeScope.first().queryFragment);

    const data = await ApplicationModel.mutate(fragment);

    return modelAttribute(data.inviteUserToMeeting.meetingAttendee, {
      query: attendeeScope.first(),
    }) as MeetingAttendee;
  }

  get searchKey() {
    // Use the local object for caching, which is already part of ApplicationModel
    if (this.local.searchKey !== undefined) return this.local.searchKey;

    this.local.searchKey = String(this.nameOrDefault).toLocaleLowerCase();

    return this.local.searchKey;
  }
}

export default Page;
